import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
  FormControl,
} from '@angular/forms'
import { CommonModule } from '@angular/common'
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatRadioModule } from '@angular/material/radio'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatOptionModule } from '@angular/material/core'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material/core'
import { MatTooltipModule } from '@angular/material/tooltip'
import { TranslationService } from '../../../services/translation.service'
import { CommonService } from '../../../services/common.service'
import { ProductService } from '../../../services/product.service'
import { FprCategoryService } from '../../../services/fprCategory.service'
import { NapcsCode, PlasticResinForm } from '../../../types/plastics'
import {
  PlasticsDeclarationFormData,
  ProductPlasticsDeclarationForm,
} from '../../../types/products'
import { NapcsCodeService } from '../../../services/napcsCode.service'
import { RouteService } from '../../../services/route.service'
import { ROUTES } from '../../../constants/routes'
import { firstValueFrom } from 'rxjs'
import { SpinnerService } from '../../../services/spinner.service'
import { ApiError } from '../../../types/apiError'
import { ErrorService } from '../../../services/error.service'
import { DialogService } from '../../../services/dialog.service'
import { Dialog } from '@angular/cdk/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { MatChipsModule } from '@angular/material/chips'
import {
  CALCULATION_METHOD_SPECIFIC_COMPONENT_IDENTIFICATION,
  CALCULATION_METHOD_AVERAGE_BILL_OF_MATERIALS,
  CALCULATION_METHOD_FIXED_FACTOR,
  CALCULATION_METHOD_OTHER,
} from '../../../constants/reports'
import {
  PRODUCT_MATERIAL_TYPE_RAW_RESIN,
  PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT,
  PRODUCT_MATERIAL_TYPE_NON_PLASTIC,
  PRODUCT_MATERIAL_TYPE_UNDECLARED,
  WASTE_STREAM_LABELS,
  WASTE_STREAM_VALUES,
  WasteStreamValue,
} from '../../../constants/plastics-declaration'
import { PlasticResinComponent } from './plastic-resin/plastic-resin.component'
import { ComponentFormData } from '../../../types/components'
import { COMMA, ENTER } from '@angular/cdk/keycodes'

@Component({
  selector: 'app-product-plastics-declaration-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatCheckboxModule,
    MatOptionModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    PlasticResinComponent,
  ],
  templateUrl: './product-plastics-declaration-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-plastics-declaration-form.component.scss',
})
export class ProductPlasticsDeclarationFormComponent
  implements OnInit, OnChanges
{
  private fb = inject(FormBuilder)
  translationService = inject(TranslationService)
  private napcsCodeService = inject(NapcsCodeService)
  private routeService = inject(RouteService)
  private spinnerService = inject(SpinnerService)
  private errorService = inject(ErrorService)
  private dialogService = inject(DialogService)
  private dialog = inject(Dialog)
  private productService = inject(ProductService)
  private commonService = inject(CommonService)
  private fprCategoryService = inject(FprCategoryService)

  public PRODUCT_MATERIAL_TYPE_RAW_RESIN = PRODUCT_MATERIAL_TYPE_RAW_RESIN
  public PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT =
    PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT
  public PRODUCT_MATERIAL_TYPE_NON_PLASTIC = PRODUCT_MATERIAL_TYPE_NON_PLASTIC
  public readonly WASTE_STREAM_LABELS = WASTE_STREAM_LABELS
  public readonly WASTE_STREAM_VALUES = WASTE_STREAM_VALUES

  public readonly tagSeparatorKeysCodes = [ENTER, COMMA] as const
  public readonly tagsTooltip =
    this.translationService.getProductTagsTooltipText()

  public readonly CALCULATION_METHODS: Record<string, string>
  public readonly SOURCE_TYPES: Record<string, string>

  public categories: Record<string, string> = {}
  private subcategoriesMap: Record<string, Record<string, string>> =
    Object.create(null)

  @Input() isPlasticPackaging: boolean | null = null
  @Input() clonedData: PlasticsDeclarationFormData | undefined
  @Input() components: ComponentFormData['components'] = []
  @Input() componentGroups: ComponentFormData['componentGroups'] = []
  @Output() formChanged = new EventEmitter<ProductPlasticsDeclarationForm>()
  @Output() backPressed = new EventEmitter()

  plasticsForm: FormGroup
  napcsCodes: Array<NapcsCode> = []
  maxReportingDate = new Date(2026, 12, 31)

  // keep a local copy of subcategories for the selected category
  public subcategoriesForSelectedCategory: Record<string, string> =
    Object.create(null)

  private readonly REQUIRED_FIELDS_FOR_RESIN = [
    'reportingDate',
    'supplier',
    'napcsCodeId',
    'sourceType',
    'productWeightKg',
  ]
  private readonly REQUIRED_FIELDS_FOR_PLASTIC = [
    'reportingDate',
    'supplier',
    'category',
    'subcategory',
    'calculationMethod',
    'wasteStream',
  ]

  constructor() {
    this.plasticsForm = this.fb.group({
      productMaterialType: ['', Validators.required],
      napcsCodeId: ['', Validators.required],
      productWeightKg: [
        0,
        [
          Validators.required,
          Validators.pattern(/^[+]?([0-9]+(?:[.][0-9]*)?|\.[0-9]+)$/),
        ],
      ],
      sourceType: ['', Validators.required],
      wasteStream: ['', Validators.required],
      supplier: ['', Validators.required],
      category: ['', Validators.required],
      subcategory: ['', Validators.required],
      calculationMethod: ['', Validators.required],
      calculationMethodOther: [''],
      reportingDate: [null, Validators.required],
      tags: this.fb.array([]),

      // used only for plastic products to hold more than one resin
      plasticResins: this.fb.array([]),
    })

    this.setupCalculationMethodDependency()

    this.syncCategoryMaps()

    this.CALCULATION_METHODS = {
      [CALCULATION_METHOD_SPECIFIC_COMPONENT_IDENTIFICATION]:
        this.translationService.getCalculationMethodSpecificText(),
      [CALCULATION_METHOD_AVERAGE_BILL_OF_MATERIALS]:
        this.translationService.getCalculationMethodAbomText(),
      [CALCULATION_METHOD_FIXED_FACTOR]:
        this.translationService.getCalculationMethodFixedFactorText(),
      [CALCULATION_METHOD_OTHER]: this.translationService.getOtherText(),
    }

    this.SOURCE_TYPES = {
      'Post-Consumer Recycled':
        this.translationService.getSourceTypePostConsumerRecycledText(),
      'Post-Industrial Recycled':
        this.translationService.getSourceTypePostIndustrialRecycledText(),
      'Virgin Fossil-Based':
        this.translationService.getSourceTypeVirginFossilText(),
      'Virgin Bio-Based': this.translationService.getSourceTypeVirginBioText(),
    }
  }

  /**
   * Sets up a conditional validator that makes the 'calculationMethodOther' field required
   * only when the selected calculation method is 'Other'.
   */
  private setupCalculationMethodDependency(): void {
    this.plasticsForm
      .get('calculationMethod')
      ?.valueChanges.subscribe((method) => {
        const otherControl = this.plasticsForm.get('calculationMethodOther')
        if (method === CALCULATION_METHOD_OTHER) {
          otherControl?.setValidators([Validators.required])
        } else {
          otherControl?.clearValidators()
          otherControl?.setValue('')
        }
        otherControl?.updateValueAndValidity()
      })
  }

  // Recompute the subcategory map based on the current category and packaging mode.
  // If the currently selected subcategory is not valid for the new map, clear it.
  private syncSubcategoryMapForCurrentCategory(): void {
    const cat = (this.plasticsForm.get('category')?.value as string) ?? ''

    const { subcategoriesForSelectedCategory, sanitizedSubcategory } =
      this.fprCategoryService.getSubcategorySelection(
        cat,
        (this.plasticsForm.get('subcategory')?.value as string) ?? '',
        !!this.isPlasticPackaging,
      )

    this.subcategoriesForSelectedCategory = subcategoriesForSelectedCategory

    const currentSub =
      (this.plasticsForm.get('subcategory')?.value as string) ?? ''
    if (sanitizedSubcategory !== currentSub) {
      this.plasticsForm.patchValue(
        { subcategory: sanitizedSubcategory },
        { emitEvent: false },
      )
    }
  }

  ngOnInit(): void {
    const currentRoute = this.routeService.getUserRoutes(
      ROUTES.COMPANY_PRODUCTS.title,
      `/admin/companies/${ROUTES.COMPANY_PRODUCTS.path}`,
    )
    this.routeService.setRoute(currentRoute)

    this.fetchNapcsCodes()

    // Subscribe to productMaterialType changes to update validators
    // and, for plastic products, optionally derive resins from components (including from groups).
    this.plasticsForm
      .get('productMaterialType')
      ?.valueChanges.subscribe((value) => {
        this.updateFieldValidators(value)
        if (value === PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT) {
          const hasAnyComponents =
            (this.components && this.components.length > 0) ||
            (this.componentGroups && this.componentGroups.length > 0)

          // Only auto-derive resins if there are no existing resin rows.
          // This avoids wiping out user-entered resins when toggling material type.
          if (hasAnyComponents && this.plasticResins.length === 0) {
            this.populatePlasticResinsFromComponents()
          } else if (!hasAnyComponents && this.plasticResins.length === 0) {
            // fall back to a single empty row if we have no components
            this.plasticResins.push(this.buildEmptyResinGroup())
          }
        } else {
          this.plasticResins.clear()
        }
      })

    // Keep subcategory record in sync with selected category without calling a function in the template
    this.plasticsForm.get('category')?.valueChanges.subscribe((cat: string) => {
      const currentSub =
        (this.plasticsForm.get('subcategory')?.value as string) ?? ''

      const { subcategoriesForSelectedCategory, sanitizedSubcategory } =
        this.fprCategoryService.getSubcategorySelection(
          cat,
          currentSub,
          !!this.isPlasticPackaging,
        )

      this.subcategoriesForSelectedCategory = subcategoriesForSelectedCategory

      if (sanitizedSubcategory !== currentSub) {
        this.plasticsForm.get('subcategory')?.setValue('')
      }
    })

    const initialCat =
      (this.plasticsForm.get('category')?.value as string) ?? ''
    this.subcategoriesForSelectedCategory =
      this.fprCategoryService.getSubcategoriesForCategory(
        initialCat,
        !!this.isPlasticPackaging,
      )

    if (this.clonedData) {
      this.applyClonedData(this.clonedData)
    }
  }

  /**
   * Apply cloned plastics declaration data to the form.
   *
   * This will:
   * - normalise the reporting date
   * - maps waste stream booleans to the radio control
   * - keeps the "Other" calculation method field in a safe state
   * - handles productWeightKg and plastic resins for plastic products
   * - refresh the subcategory options for the selected category
   */
  private applyClonedData(data: PlasticsDeclarationFormData): void {
    // Tidy up the reporting date coming from the backend.
    const normalizedReportingDate = this.commonService.normalizeDate(
      (data as { reportingDate?: unknown }).reportingDate,
    )

    // Base patch for the main form values.
    const patch: Partial<ProductPlasticsDeclarationForm> = {
      ...data,
    }

    if (normalizedReportingDate) {
      patch.reportingDate = normalizedReportingDate
    }

    // Make sure the "Other" text field lines up with the selected method.
    if (patch.calculationMethod !== CALCULATION_METHOD_OTHER) {
      patch.calculationMethodOther = ''
    } else if (patch.calculationMethodOther == null) {
      patch.calculationMethodOther = ''
    }

    // For plastic products we ignore any incoming weightPerUnitKg.
    // let the control logic after handle clearing/disabling the field.
    if (data.productMaterialType === PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT) {
      delete (patch as { productWeightKg?: number }).productWeightKg
    }

    // patch the main fields
    this.plasticsForm.patchValue(patch, { emitEvent: true })

    // Convert the three waste stream flags into the single radio value.
    const wasteStreamValue = this.convertWasteStreamToRadioValue(
      !!patch.isResidentialWasteStream,
      !!patch.isIciWasteStream,
      !!patch.isCdrWasteStream,
    )

    // Always write the wasteStream control so we don't leave stale values behind.
    this.plasticsForm.patchValue(
      { wasteStream: wasteStreamValue ?? '' },
      { emitEvent: true },
    )

    this.tagsArray.clear()
    const incomingTags =
      (data as PlasticsDeclarationFormData & { tags?: string[] }).tags ?? []
    for (const tag of incomingTags) {
      const value = (tag ?? '').trim()
      if (value) {
        this.tagsArray.push(
          new FormControl<string>(value, { nonNullable: true }),
        )
      }
    }

    // Keep the weight control in a sensible state for each material type.
    const weightCtrl = this.plasticsForm.get('productWeightKg')
    if (data.productMaterialType === PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT) {
      weightCtrl?.setValue(null, { emitEvent: false })
      weightCtrl?.disable({ emitEvent: false })
    } else {
      weightCtrl?.enable({ emitEvent: false })
    }

    // Reset and repopulate the resin list for plastic products.
    this.plasticResins.clear()
    if (data.productMaterialType === PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT) {
      const resins = data.plasticResins ?? []

      const derivedResins = resins.filter((r) => r.isDerived)
      const manualResins = resins.filter((r) => !r.isDerived)

      const addResinToForm = (resin: (typeof resins)[number]) => {
        const matchingSourceKey =
          Object.keys(this.SOURCE_TYPES).find(
            (key) => key === resin.sourceType,
          ) ?? ''

        const resinGroup = this.fb.group({
          napcsCodeId: [resin.napcsCodeId ?? '', Validators.required],
          sourceType: [matchingSourceKey, Validators.required],
          weightPerUnitKg: [
            resin.weightPerUnitKg ?? null,
            [
              Validators.required,
              Validators.pattern(/^[+]?([0-9]+(?:[.][0-9]*)?|\.[0-9]+)$/),
            ],
          ],
          isDerived: [resin.isDerived ?? false],
        }) as PlasticResinForm

        if (resin.isDerived) {
          resinGroup.disable({ emitEvent: false })
        }

        this.plasticResins.push(resinGroup)
      }

      // Derived resins first
      for (const resin of derivedResins) {
        addResinToForm(resin)
      }
      // Then manual (non-derived) resins
      for (const resin of manualResins) {
        addResinToForm(resin)
      }
    }

    // Refresh subcategory options based on the current category.
    this.syncSubcategoryMapForCurrentCategory()
  }

  get tagsArray(): FormArray<FormControl<string>> {
    return this.plasticsForm.get('tags') as FormArray<FormControl<string>>
  }

  // expose the array for the template and child component
  get plasticResins(): FormArray<PlasticResinForm> {
    return this.plasticsForm.get('plasticResins') as FormArray<PlasticResinForm>
  }

  private buildEmptyResinGroup(): PlasticResinForm {
    return this.fb.group({
      napcsCodeId: ['', Validators.required],
      sourceType: ['', Validators.required],
      weightPerUnitKg: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[+]?([0-9]+(?:[.][0-9]*)?|\.[0-9]+)$/),
        ],
      ],
      isDerived: [false],
    }) as PlasticResinForm
  }

  addTag(event: {
    value: string | null
    chipInput?: { clear: () => void }
  }): void {
    const rawValue = event.value ?? ''
    const value = rawValue.trim()

    if (!value) {
      event.chipInput?.clear()
      return
    }

    const exists = this.tagsArray.controls.some(
      (ctrl) => (ctrl.value ?? '').trim().toLowerCase() === value.toLowerCase(),
    )

    if (!exists) {
      this.tagsArray.push(new FormControl<string>(value, { nonNullable: true }))
    }

    event.chipInput?.clear()
  }

  removeTag(index: number): void {
    this.tagsArray.removeAt(index)
  }

  isRawResin(): boolean {
    return (
      this.plasticsForm.get('productMaterialType')?.value ===
      PRODUCT_MATERIAL_TYPE_RAW_RESIN
    )
  }

  isPlasticProduct(): boolean {
    return (
      this.plasticsForm.get('productMaterialType')?.value ===
      PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT
    )
  }

  isNonPlastic(): boolean {
    return (
      this.plasticsForm.get('productMaterialType')?.value ===
      PRODUCT_MATERIAL_TYPE_NON_PLASTIC
    )
  }

  isUndeclared(): boolean {
    return (
      this.plasticsForm.get('productMaterialType')?.value ===
      PRODUCT_MATERIAL_TYPE_UNDECLARED
    )
  }

  private updateFieldValidators(materialType: string) {
    const allFields = new Set([
      ...this.REQUIRED_FIELDS_FOR_RESIN,
      ...this.REQUIRED_FIELDS_FOR_PLASTIC,
    ])

    allFields.forEach((field) => {
      const control = this.plasticsForm.get(field)
      if (!control) return

      const isResin = materialType === PRODUCT_MATERIAL_TYPE_RAW_RESIN
      const isPlastic = materialType === PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT
      if (
        (isResin && this.REQUIRED_FIELDS_FOR_RESIN.includes(field)) ||
        (isPlastic && this.REQUIRED_FIELDS_FOR_PLASTIC.includes(field))
      ) {
        control.setValidators([Validators.required])
      } else {
        control.clearValidators()
      }

      control.updateValueAndValidity()
    })

    // Also handle weightPerUnitKg at the top level for plastic products
    const weightCtrl = this.plasticsForm.get('productWeightKg')
    const isPlastic = materialType === PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT
    if (weightCtrl) {
      if (isPlastic) {
        weightCtrl.setValue(null, { emitEvent: false })
        weightCtrl.disable({ emitEvent: false })
      } else {
        weightCtrl.enable({ emitEvent: false })
      }
    }
  }

  async fetchNapcsCodes(): Promise<void> {
    try {
      this.spinnerService.startSpinner()
      const codes = await firstValueFrom(this.napcsCodeService.getAll())
      this.napcsCodes = codes
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const packagingChange = changes['isPlasticPackaging']
    const clonedChange = changes['clonedData']
    const componentsChange = changes['components']
    const componentGroupsChange = changes['componentGroups']

    // if is plastic packaging changed AND we already have cloned data, reapply it right away
    if (packagingChange && this.clonedData) {
      this.syncCategoryMaps()
      this.applyClonedData(this.clonedData)
      return
    }

    // if new cloned data arrived, apply it
    if (clonedChange && clonedChange.currentValue) {
      this.applyClonedData(
        clonedChange.currentValue as PlasticsDeclarationFormData,
      )
      return
    }

    // if components or component groups changed while the product is a plastic product,
    // refresh only the derived resin rows from the backend and keep manual rows intact.
    const isPlasticProductSelected =
      this.plasticsForm.get('productMaterialType')?.value ===
      PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT

    if (
      isPlasticProductSelected &&
      (componentsChange || componentGroupsChange)
    ) {
      const hasAnyComponents =
        (this.components && this.components.length > 0) ||
        (this.componentGroups && this.componentGroups.length > 0)

      if (hasAnyComponents) {
        void this.populatePlasticResinsFromComponents()
      } else {
        // If there are no components at all, drop any derived rows and keep only manual ones.
        this.removeDerivedResins()
      }
    }

    // otherwise just resync maps/subcategories if packaging mode changed
    if (packagingChange) {
      this.syncCategoryMaps()
      this.syncSubcategoryMapForCurrentCategory()
    }
  }

  /**
   * Remove all derived resin rows while keeping any manual rows intact.
   */
  private removeDerivedResins(): void {
    const manualValues = this.plasticResins.controls
      .filter((group) => !group.get('isDerived')?.value)
      .map((group) => ({
        napcsCodeId: group.get('napcsCodeId')?.value ?? '',
        sourceType: group.get('sourceType')?.value ?? '',
        weightPerUnitKg: group.get('weightPerUnitKg')?.value ?? null,
      }))

    this.plasticResins.clear()

    for (const value of manualValues) {
      const manualGroup = this.fb.group({
        napcsCodeId: [value.napcsCodeId, Validators.required],
        sourceType: [value.sourceType, Validators.required],
        weightPerUnitKg: [
          value.weightPerUnitKg,
          [
            Validators.required,
            Validators.pattern(/^[+]?([0-9]+(?:[.][0-9]*)?|\.[0-9]+)$/),
          ],
        ],
        isDerived: [false],
      }) as PlasticResinForm

      this.plasticResins.push(manualGroup)
    }
  }

  /**
   * Checks for duplicate tags (case-insensitive, trimmed).
   */
  private hasDuplicateTags(): boolean {
    const seen = new Set<string>()

    for (const tag of this.tagsArray.getRawValue()) {
      const normalized = (tag ?? '').trim().toLowerCase()
      if (!normalized) {
        continue
      }

      if (seen.has(normalized)) {
        return true
      }

      seen.add(normalized)
    }

    return false
  }

  /**
   * Emits form data
   */
  async onSubmit() {
    this.plasticsForm.markAllAsTouched()

    if (this.hasDuplicateTags()) {
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getTagsText(),
        this.translationService.getDuplicateProductTagsError(),
      )
      return
    }

    if (!this.plasticsForm.valid) {
      return
    }

    const formValue =
      this.plasticsForm.getRawValue() as ProductPlasticsDeclarationForm & {
        wasteStream: WasteStreamValue
      }
    const wasteStreamBooleans = this.convertRadioValueToWasteStreams(
      formValue.wasteStream,
    )

    // Remove wasteStream from form value and add boolean fields
    const formValueWithoutWasteStream = Object.fromEntries(
      Object.entries(formValue).filter(([key]) => key !== 'wasteStream'),
    ) as Omit<typeof formValue, 'wasteStream'>

    this.formChanged.emit({
      ...formValueWithoutWasteStream,
      ...wasteStreamBooleans,
      tags: this.tagsArray
        .getRawValue()
        .filter((tag): tag is string => !!tag?.trim()),
      formName: 'plasticsDeclarationForm',
    })
  }

  /**
   * Resets the form to default values
   */
  onReset() {
    this.plasticsForm.reset({
      napcsCodeId: '',
      weightPerUnitKg: 0,
      sourceType: '',
      wasteStream: '',
      supplier: '',
      category: '',
      subcategory: '',
      calculationMethod: '',
      calculationMethodOther: '',
      reportingDate: null,
      plasticResins: [],
      tags: [],
    })
    this.plasticResins.clear()
    this.tagsArray.clear()
  }

  /**
   * Handles the click of the back button to navigate to previous form
   */
  goBack() {
    this.backPressed.emit(null)
  }

  get selectedSubcategories(): string[] {
    return Object.keys(this.subcategoriesForSelectedCategory)
  }

  get activeCategories(): Record<string, string> {
    return this.categories
  }

  get activeSubcategoriesMap(): Record<string, Record<string, string>> {
    return this.subcategoriesMap
  }

  hasMaterialType(): boolean {
    return !!this.plasticsForm.get('productMaterialType')?.value
  }

  /**
   * Converts three boolean waste stream fields to a single radio value
   */
  private convertWasteStreamToRadioValue(
    isResidential: boolean,
    isIci: boolean,
    isCdr: boolean,
  ): WasteStreamValue | null {
    if (isResidential) return WASTE_STREAM_VALUES.RESIDENTIAL
    if (isIci) return WASTE_STREAM_VALUES.ICI
    if (isCdr) return WASTE_STREAM_VALUES.CDR
    return null
  }

  /**
   * Converts a radio value to three boolean waste stream fields
   */
  private convertRadioValueToWasteStreams(radioValue: WasteStreamValue) {
    return {
      isResidentialWasteStream: radioValue === WASTE_STREAM_VALUES.RESIDENTIAL,
      isIciWasteStream: radioValue === WASTE_STREAM_VALUES.ICI,
      isCdrWasteStream: radioValue === WASTE_STREAM_VALUES.CDR,
    }
  }

  /**
   * Calls the backend to derive plastic resins from the current components (including groups)
   * and refreshes only the derived rows in the form. Manual (non-derived) rows are preserved.
   */
  private async populatePlasticResinsFromComponents(): Promise<void> {
    // Ensure NAPCS codes are loaded before we try to map resinType -> napcsCodeId.
    if (!this.napcsCodes || this.napcsCodes.length === 0) {
      try {
        const codes = await firstValueFrom(this.napcsCodeService.getAll())
        this.napcsCodes = codes
      } catch (err) {
        const apiErr = err as ApiError
        this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
        // If we can't load codes, we can't reliably map resins to dropdown options.
        return
      }
    }

    // Build a simplified list of components for the derive call,
    // including groupIndex so the backend can apply ABOM logic.
    const deriveComponents = []

    // Ungrouped components: always present, no groupIndex.
    if (this.components && this.components.length > 0) {
      for (const c of this.components) {
        // Defensive: only send components with a valid id and quantity > 0.
        if (!c.id || !c.quantity || c.quantity <= 0) {
          continue
        }

        deriveComponents.push({
          componentId: c.id,
          quantity: c.quantity,
          // Ungrouped rows do not participate in ABOM groups.
          groupIndex: undefined,
        })
      }
    }

    // Grouped components: each groupIndex identifies an interchangeable group.
    if (this.componentGroups && this.componentGroups.length > 0) {
      this.componentGroups.forEach((group, groupIndex) => {
        const groupComponents = group.components ?? []
        for (const c of groupComponents) {
          if (!c.id || !c.quantity || c.quantity <= 0) {
            continue
          }

          deriveComponents.push({
            componentId: c.id,
            quantity: c.quantity,
            // If the row already has a groupIndex, keep it; otherwise fall back to the group index here.
            groupIndex: c.groupIndex ?? groupIndex,
          })
        }
      })
    }

    const hasAnyComponents = deriveComponents.length > 0
    if (!hasAnyComponents) {
      // No components at all: drop derived rows and leave manual rows as-is.
      this.removeDerivedResins()
      return
    }

    try {
      this.spinnerService.startSpinner()

      // Snapshot manual (non-derived) rows so we can restore them.
      const manualValues = this.plasticResins.controls
        .filter((group) => !group.get('isDerived')?.value)
        .map((group) => ({
          napcsCodeId: group.get('napcsCodeId')?.value ?? '',
          sourceType: group.get('sourceType')?.value ?? '',
          weightPerUnitKg: group.get('weightPerUnitKg')?.value ?? null,
        }))

      const aggregatedResins = await firstValueFrom(
        this.productService.getPlasticResinsFromComponents(deriveComponents),
      )

      // Rebuild the resin list.
      this.plasticResins.clear()

      // First, add fresh derived rows from the backend (these should always appear first).
      if (aggregatedResins && aggregatedResins.length > 0) {
        for (const resin of aggregatedResins) {
          const matchingCode = this.napcsCodes.find(
            (code) => code.fullResinTitle === resin.resinType,
          )

          const matchingSourceKey =
            Object.keys(this.SOURCE_TYPES).find(
              (key) => key === resin.sourceType,
            ) ?? ''

          const resinGroup = this.fb.group({
            napcsCodeId: [matchingCode?.id ?? '', Validators.required],
            sourceType: [matchingSourceKey, Validators.required],
            weightPerUnitKg: [
              resin.weightPerUnitKg ?? null,
              [
                Validators.required,
                Validators.pattern(/^[+]?([0-9]+(?:[.][0-9]*)?|\.[0-9]+)$/),
              ],
            ],
            // rows produced by this call are always derived from components
            isDerived: [resin.isDerived ?? true],
          }) as PlasticResinForm

          this.plasticResins.push(resinGroup)

          if (resinGroup.get('isDerived')?.value) {
            resinGroup.disable({ emitEvent: false })
          }
        }
      }

      // Then restore manual rows exactly as the user left them, after derived rows.
      for (const manual of manualValues) {
        const manualGroup = this.fb.group({
          napcsCodeId: [manual.napcsCodeId, Validators.required],
          sourceType: [manual.sourceType, Validators.required],
          weightPerUnitKg: [
            manual.weightPerUnitKg,
            [
              Validators.required,
              Validators.pattern(/^[+]?([0-9]+(?:[.][0-9]*)?|\.[0-9]+)$/),
            ],
          ],
          isDerived: [false],
        }) as PlasticResinForm

        this.plasticResins.push(manualGroup)
      }

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
      // In case of error, leave manual rows intact; don't add derived rows.
    }
  }

  private syncCategoryMaps(): void {
    const isPackaging = !!this.isPlasticPackaging
    this.categories = this.fprCategoryService.getCategories(isPackaging)
    this.subcategoriesMap =
      this.fprCategoryService.getSubcategories(isPackaging)
  }
}
